'use client'

import { css } from '@lastbrand/web-ui-styled-system/css'
import { useTranslation } from 'next-i18next'
import { Fragment, useMemo } from 'react'

import { BundleProductCardPriceAndTitleV2DS } from '@src/components/blocks/bundle-product-card-price-and-title-v2-ds'
import { mergeLegacyResponsiveStyles } from '@src/components/blocks/rich-text-ds/classname-to-ds-style'
import SiblingProductOptionsDS, {
  SiblingProductOptionType,
} from '@src/components/blocks/sibling-product-options-ds/sibling-product-options-ds'
import SizeChartButtonDS from '@src/components/blocks/size-chart-button-ds/size-chart-button-ds'
import { EMPTY_ARROW_FUNC } from '@src/config/constants'
import { STATSIG_EXPERIMENTS, STATSIG_EXPERIMENTS_BUCKETS } from '@src/config/statsig-experiments.config'
import {
  matchSlugTargets,
  STATSIG_EXPERIMENT_TARGETS_CONFIG_KEYS,
} from '@src/experiments/statsig-integration/experiment-targets.types'
import type { SlugExperimentTarget } from '@src/experiments/statsig-integration/experiment-targets.types'
import { useTargetedStatsigExperiment } from '@src/experiments/statsig-integration/use-targeted-statsig-experiment'
import { Image } from '@src/quince-components/src'
import { renderProductOption } from '@src/quince-components/src/atoms/productForm/ProductForm'
import { useSvdOptionRows } from '@src/quince-components/src/atoms/productForm/use-svd-option-rows'
import { getDiscountedPrice } from '@src/quince-components/src/molecules/productCard/productCard.helpers'
import { ProductOptionDisplayType, ProductOptionName } from '@src/quince-site-common/src/domains/ProductOption'
import type SvdProduct from '@src/quince-site-common/src/domains/svd/svd-product'
import type { SvdSelection } from '@src/quince-site-common/src/domains/svd/svd-product.types'
import productHelper from '@src/quince-site-common/src/helpers/productHelper'
import { resolveSelectedVariant } from '@src/quince-site-common/src/helpers/svd-cascade-helper'
import useSizeGuideSvdExperiment from '@src/quince-site-common/src/hooks/use-size-guide-svd-experiment'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import type { BundleProductCardProps } from '@src/quince-site-pdp/src/molecules/bundleProductCard/bundle-product-card-types'

import {
  addOnBundleCss,
  addOnProductImageContainerCss,
  bundleCardCss,
  bundleCardWrapperCss,
  errorMessageCss,
  imageContainerCss,
  productDetailsCss,
  productOptionsContainerCss,
  productOptionsSpacingCss,
  siblingOptionsSelectorCss,
  sizeChartWrapperCss,
  textOptionLabelNewDesignV2Css,
} from './bundle-product-card-ds.styles'

const OPTION_DISPLAY_TYPES_VS_CLASS_NAME_DS = {
  [ProductOptionDisplayType.TEXT]: textOptionLabelNewDesignV2Css,
}

/** Stable empty literals — module-scope so the SVD branches never re-arm memos on a new reference. */
// Typed off the helper's own return so the SVD branch can't widen `productOptions` into a union.
const EMPTY_PRODUCT_OPTIONS: ReturnType<typeof productHelper.getCascadingProductOptions> = []
const EMPTY_SVD_SELECTED: SvdSelection[] = []

function BundleProductCardDS({
  product,
  selectedOptions,
  salePrice,
  discount,
  isAddOnProduct = false,
  expanded = true,
  selectedSiblingProductId,
  error = null,
  onSiblingOptionSelect,
  setExpanded,
  onOptionSelect,
  showSizeChart,
  isSvd = false,
  svdSelected,
  serverVariantId,
  onSvdOptionSelect,
  'data-testid': dataTestId,
}: BundleProductCardProps) {
  const deviceInfo = useDeviceInfo()
  const { t: tCommon } = useTranslation('common')
  const isMobile = deviceInfo.deviceType === DEVICE_TYPES.MOBILE
  const discountedSalePrice = useMemo(() => {
    return getDiscountedPrice(salePrice, discount)
  }, [salePrice, discount, selectedOptions])

  const { experimentBucket: imageExperimentBucket } = useTargetedStatsigExperiment(
    STATSIG_EXPERIMENTS.PDP__PRODUCT_IMAGES_IMAGES_EXPERIMENTATION__ALL_US_CA,
    STATSIG_EXPERIMENT_TARGETS_CONFIG_KEYS[STATSIG_EXPERIMENTS.PDP__PRODUCT_IMAGES_IMAGES_EXPERIMENTATION__ALL_US_CA],
    (targets: SlugExperimentTarget[]) => matchSlugTargets(targets, product.slug),
  )

  // SVD carries no legacy `productOptions`; its rows come from `svdOptionRows` below.
  const productOptions = isSvd
    ? EMPTY_PRODUCT_OPTIONS
    : productHelper.getCascadingProductOptions(product, selectedOptions)
  const optionNameSelectedValueMap = selectedOptions.reduce((map: Record<string, string>, option) => {
    map[option.name] = option.value
    return map
  }, {})

  const chooseProductText = isAddOnProduct ? `${tCommon('add_a')} ${product.title}` : product.title

  // Per-card gate. The `show` bucket of PDP_SIZE_GUIDE_SVD puts this constituent on the V3 guide, which
  // renders from `product.sizeGuide` alone — so a constituent without one has nothing to open and hides
  // its button (just that card, never the others). The `control` bucket keeps the legacy size-chart
  // behaviour untouched.
  const hasSizeGuide = !!product.sizeGuide
  const hasSizeChart = Boolean(product.sizeChart || product.mergedSizeChart)
  const isSizeGuideSvd = useSizeGuideSvdExperiment()
  const shouldShowSizeChartButton = isSizeGuideSvd ? hasSizeGuide : hasSizeChart

  // STRFRNT-16842: the button belongs in the Size row's header (`ProductPropertyDS`'s `action`
  // slot, Figma `hasButton`) — beside the tiles — exactly as every non-bundle PDP renders it via
  // `renderProductOption`. This card used to force `hideSizeChart: true` and drop a standalone
  // button AFTER the options container instead, which is what put it below the tiles.
  //
  // The header slot's own gate is `!hideSizeChart && name === Size && (sizeChart ?? mergedSizeChart)`,
  // so it can only render for a visible Size option backed by a contentful chart. `useSvdOptionRows`
  // has no size-chart slot at all (unlike the buy-box's `useSvdOptionNodes`), so SVD cards keep the
  // standalone button. Mirrors `shouldShowOnlySizeChart`: whichever affordance can't render, the
  // other one does — never two, never none.
  const sizeOption = productOptions.find(({ name }) => name === ProductOptionName.Size)
  const showSizeChartInSizeHeader =
    !isSvd && shouldShowSizeChartButton && hasSizeChart && !!sizeOption && !sizeOption.isHidden

  // SVD VDA rows for this constituent. `renderVdaOption` (inside the builder) routes DS vs legacy itself,
  // so this call yields DS-styled rows here and SCSS rows in the legacy card — one code path, both branches.
  const svdOptionRows = useSvdOptionRows({
    enabled: isSvd,
    product: product as unknown as SvdProduct,
    svdSelected: svdSelected ?? EMPTY_SVD_SELECTED,
    onOptionSelect: onSvdOptionSelect ?? EMPTY_ARROW_FUNC,
    // Engraving is a buy-box concern — bundle cards are selection-only (V1 parity).
    includeEngraving: false,
    // On-sale red-dot stays off here, mirroring the legacy branch's `isBundle: true` below (legacy gates the
    // cue on `isPDP && !isBundle`). Left explicit rather than relying on the hook default.
    showOnSaleRedDot: false,
    productFormOptionsClass: productOptionsSpacingCss,
    t: tCommon,
  })

  const productMedia = useMemo(() => {
    // SVD resolves its thumbnail off the variant (below), and an SVD product carries no legacy `images`.
    if (isSvd) return []
    const experimentImages =
      imageExperimentBucket === STATSIG_EXPERIMENTS_BUCKETS.GROUP_A ||
      imageExperimentBucket === STATSIG_EXPERIMENTS_BUCKETS.GROUP_B
        ? productHelper.getImageExperimentForOptions(product, selectedOptions, {
            bucket: imageExperimentBucket,
            name: STATSIG_EXPERIMENTS.PDP__PRODUCT_IMAGES_IMAGES_EXPERIMENTATION__ALL_US_CA,
          })
        : undefined
    return productHelper.getImagesForOptions(product, selectedOptions, undefined, experimentImages)
  }, [product, selectedOptions, imageExperimentBucket, isSvd])

  // SVD thumbnail: the resolved variant's hero image (server-resolved variant first, then min-price) —
  // keeps the card image consistent with the price shown beside it.
  const imageUrl = isSvd
    ? (resolveSelectedVariant(product, svdSelected ?? EMPTY_SVD_SELECTED, serverVariantId)?.heroImageUrl ?? '')
    : productMedia.length
      ? (productMedia[0]?.image.url ?? '')
      : ''

  const renderBundleProductDetailsSection = () => {
    const productOptionsRenderer = productOptions
      .filter(po => !po.isHidden)
      .map(productOption => ({
        node: renderProductOption({
          productOption: productOption,
          optionNameSelectedValueMap: optionNameSelectedValueMap,
          onOptionSelect: onOptionSelect,
          hideSizeChart: !showSizeChartInSizeHeader,
          sizeChart: showSizeChartInSizeHeader ? product.sizeChart : undefined,
          handleSizeChart: showSizeChart ?? EMPTY_ARROW_FUNC,
          renderSwatchSelectionButton: () => null,
          product: product,
          selectedOptions: selectedOptions,
          optionsContainerClassName: productOptionsSpacingCss,
          mergedSizeChart: showSizeChartInSizeHeader ? product.mergedSizeChart : undefined,
          legendClassName: undefined,
          optionSelectorClassName: undefined,
          optionDisplayTypesVsClassName: OPTION_DISPLAY_TYPES_VS_CLASS_NAME_DS,
          t: tCommon,
          noMarginForSizeOption: productOption.name === ProductOptionName.Size,
          isBundle: true,
        }),
        displaySequence: productOption.displaySequence,
        key: productOption.name,
      }))

    const addOnOptionsRenderer = product.addOnProductOptionGroups?.map(group => {
      return {
        node: (
          <SiblingProductOptionsDS
            siblingProductOptionGroup={group}
            displayType={onSiblingOptionSelect ? SiblingProductOptionType.BUTTON : SiblingProductOptionType.LINK}
            selectedProductId={selectedSiblingProductId}
            onSelect={onSiblingOptionSelect}
            optionSelectorClassName={siblingOptionsSelectorCss}
            className={productOptionsSpacingCss}
          />
        ),
        displaySequence: group.optionDisplaySequence,
        key: group.groupId,
      }
    })

    // SVD swaps the legacy option rows for the VDA rows; merge (sibling) rows are shared by both flows —
    // V2 still carries `addOnProductOptionGroups` for set-type merges.
    const combinedProductOptions = [
      ...(addOnOptionsRenderer || []),
      ...(isSvd ? svdOptionRows : productOptionsRenderer),
    ]
    combinedProductOptions.sort((a, b) => a.displaySequence - b.displaySequence)
    return (
      expanded && (
        <>
          <div className={productOptionsContainerCss}>
            {combinedProductOptions.map(option => (
              <Fragment key={option.key}>{option.node}</Fragment>
            ))}
          </div>
          {shouldShowSizeChartButton && showSizeChart && !showSizeChartInSizeHeader && (
            <div className={sizeChartWrapperCss}>
              <SizeChartButtonDS onClick={showSizeChart} />
            </div>
          )}
          {error && <div className={errorMessageCss}>{error}</div>}
        </>
      )
    )
  }

  return (
    <div
      data-testid={dataTestId}
      className={css(mergeLegacyResponsiveStyles(bundleCardWrapperCss, isAddOnProduct ? addOnBundleCss : {}))}
    >
      <div className={bundleCardCss}>
        <div
          className={css(
            mergeLegacyResponsiveStyles(
              imageContainerCss,
              // STRFRNT-16915/16944: the add-on override applies in BOTH collapsed and expanded
              // states (Figma AddOnList 13040:28056 / ProductTileBundle 13046:924). Both variants
              // now share the same 96x122.667px image column, so the override carries only the
              // add-on-specific deltas — the state-independence is the point, not the size.
              isAddOnProduct ? addOnProductImageContainerCss : {},
            ),
          )}
        >
          <Image url={imageUrl} title={product.title} maxWidth={250} overrideMaxImageWidth1600 alt={product.title} />
        </div>
        <div className={productDetailsCss}>
          <BundleProductCardPriceAndTitleV2DS
            chooseProductText={chooseProductText}
            discountedSalePrice={discountedSalePrice}
            salePrice={salePrice}
            isAddOnProduct={isAddOnProduct}
            expanded={expanded}
            setExpanded={setExpanded || EMPTY_ARROW_FUNC}
          />

          {!isMobile && renderBundleProductDetailsSection()}
        </div>
      </div>
      {isMobile && renderBundleProductDetailsSection()}
    </div>
  )
}

export default BundleProductCardDS
